import { observable } from 'mobx'
import { CommonContainer } from 'src/models/Common'
import { savedCardsContainer } from 'src/models/TokenEx'
import { paymetricContainer } from './Paymetric'

/**
 * Paymetric CVV-only container for saved cards.
 *
 * Exposes TokenEx-compatible methods used by saved card flows:
 * - loadTokenExCvvIframe(...)
 * - getToken(callback)
 */
class PaymetricSavedCardCvvContainer extends CommonContainer {
  @observable tokenExCvvData = {}
  @observable tokenCvvValidData = {}
  formSubmitCallBack = null
  errorValidationCallBack = null
  containerId = null
  cvvIframeData = {}
  paymetricCvvSession = null
  paymetricCvvIframeId = ''

  trackPaymetricSavedCardCvvError = options => {
    const { errorReport, errorData } = options || {}
    console.warn(errorReport, errorData)
    if (typeof ineum === 'function') {
      ineum('reportEvent', `Paymetric: ${errorReport}`, {
        meta: errorData,
      })
    }
  }

  waitForXiFrame = async () => {
    let retries = 15
    while (retries > 0) {
      if (
        typeof window !== 'undefined' &&
        typeof window.$XIFrame !== 'undefined'
      ) {
        return true
      }
      await new Promise(resolve => setTimeout(resolve, 200))
      retries -= 1
    }
    return false
  }

  getPaymetricSavedCardCvvAccessToken = async () => {
    const loadParams = {
      endPointName: 'getGpsIframeAccessToken',
      postData: {
        sessionRequestType: 1,
        merchantDevelopmentEnvironment: 'javascript',
        xmlPacket: paymetricContainer.buildPaymetricXmlPacket('cvv'),
      },
      credentials: 'include',
      isToSendLocaleInQueryParam: false,
      isToSendStoreId: true,
      isToIncludeCommomHeaders: false,
    }

    try {
      const response = await this.fetchResponse(loadParams)
      const payload = response?.payload || response
      const iframeUrl = payload?.iframeUrl || payload?.IframeUrl || ''
      const accessToken = payload?.accessToken || payload?.AccessToken || ''
      const merchantGuid = payload?.merchantGuid || payload?.MerchantGuid || ''

      if (!iframeUrl || !accessToken) {
        this.trackPaymetricSavedCardCvvError({
          errorReport: 'Failed to get Paymetric saved-card CVV session',
          errorData: response,
        })
        return null
      }

      return {
        merchantGuid,
        accessToken,
        iframeUrl,
      }
    } catch (error) {
      this.trackPaymetricSavedCardCvvError({
        errorReport: 'Error getting Paymetric saved-card CVV session',
        errorData: error,
      })
      return null
    }
  }

  setCvvValidity = (isValid = false, shouldNotify = true) => {
    this.tokenCvvValidData = { isValid }
    savedCardsContainer.tokenCvvValidData = this.tokenCvvValidData
    if (shouldNotify && this.errorValidationCallBack) {
      this.errorValidationCallBack(this.containerId, { isValid })
    }
  }

  retryPaymetricSavedCardCvvSession = async () => {
    const { iframeID, tokenValidate, paymentData } = this.cvvIframeData || {}
    if (!iframeID) {
      return
    }
    await this.loadTokenExCvvIframe(
      iframeID,
      tokenValidate,
      undefined,
      paymentData
    )
  }

  buildSavedCardProperties = () => {
    const { tokenValidate = '', paymentData = {} } = this.cvvIframeData || {}
    const properties = paymentData?.properties || {}

    const token =
      properties?.token ||
      properties?.Token ||
      properties?.paymetricToken ||
      properties?.PaymetricToken ||
      paymentData?.accountInfo?.token ||
      tokenValidate ||
      ''

    const referenceNumber =
      properties?.referenceNumber || properties?.ReferenceNumber || ''
    const tokenHMAC =
      properties?.tokenHMAC ||
      properties?.TokenHMAC ||
      properties?.tokenHmac ||
      ''

    return {
      ...(token && { token }),
      ...(referenceNumber && { referenceNumber }),
      ...(tokenHMAC && { tokenHMAC }),
      cvvIncluded:
        typeof properties?.cvvIncluded === 'boolean'
          ? properties.cvvIncluded
          : false,
    }
  }

  loadTokenExCvvIframe = async (
    iframeID,
    tokenValidate,
    _cardProvider,
    paymentData = {}
  ) => {
    this.cvvIframeData = {
      iframeID,
      tokenValidate,
      paymentData,
    }

    this.tokenExCvvData = {}
    savedCardsContainer.tokenExCvvData = {}
    this.setCvvValidity(false, false)

    paymetricContainer.loadPaymetricScript()
    const isXiFrameReady = await this.waitForXiFrame()
    if (!isXiFrameReady) {
      this.trackPaymetricSavedCardCvvError({
        errorReport: 'XIFrame script not ready for saved-card CVV',
        errorData: { iframeID },
      })
      this.setCvvValidity(false, false)
      return
    }

    const cvvSession = await this.getPaymetricSavedCardCvvAccessToken()
    if (!cvvSession) {
      this.setCvvValidity(false, false)
      return
    }

    this.paymetricCvvSession = cvvSession
    this.paymetricCvvIframeId = `paymetricSavedCvvIframe_${
      this.containerId || 'saved'
    }`

    try {
      const container = document.getElementById(iframeID)
      if (!container) {
        this.trackPaymetricSavedCardCvvError({
          errorReport: 'Saved-card CVV iframe container not found',
          errorData: { iframeID },
        })
        this.setCvvValidity(false, false)
        return
      }

      container.replaceChildren()
      const iframe = document.createElement('iframe')
      iframe.id = this.paymetricCvvIframeId
      iframe.src = cvvSession.iframeUrl
      iframe.width = '100%'
      iframe.height = '44'
      iframe.frameBorder = '0'
      iframe.scrolling = 'no'
      iframe.style.display = 'block'
      iframe.title = 'Paymetric Saved Card CVV'
      container.appendChild(iframe)
    } catch (error) {
      this.trackPaymetricSavedCardCvvError({
        errorReport: 'Error loading Paymetric saved-card CVV iframe',
        errorData: error,
      })
      this.setCvvValidity(false, false)
    }
  }

  getToken = cbk => {
    this.formSubmitCallBack = cbk
    this.submitPaymetricSavedCardCvvIframe()
  }

  submitPaymetricSavedCardCvvIframe = async () => {
    if (
      !this.paymetricCvvSession?.iframeUrl ||
      !this.paymetricCvvIframeId ||
      typeof window === 'undefined' ||
      typeof window.$XIFrame === 'undefined'
    ) {
      this.trackPaymetricSavedCardCvvError({
        errorReport: 'Paymetric saved-card CVV iframe not ready for submit',
        errorData: {
          hasSession: !!this.paymetricCvvSession,
          hasIframeId: !!this.paymetricCvvIframeId,
        },
      })
      this.setCvvValidity(false)
      this.formSubmitCallBack = null
      return
    }

    try {
      const result = await new Promise(resolve => {
        window.$XIFrame.submit({
          iFrameId: this.paymetricCvvIframeId,
          targetUrl: this.paymetricCvvSession.iframeUrl,
          onSuccess: msg => {
            try {
              resolve(JSON.parse(msg))
            } catch (_) {
              resolve({ data: { HasPassed: true } })
            }
          },
          onError: error => {
            resolve({
              data: { HasPassed: false },
              error,
            })
          },
        })
      })

      if (!result?.data?.HasPassed) {
        this.setCvvValidity(false)
        this.formSubmitCallBack = null
        await this.retryPaymetricSavedCardCvvSession()
        return
      }

      const savedCardTokenData = this.buildSavedCardProperties()
      this.tokenExCvvData = savedCardTokenData
      savedCardsContainer.tokenExCvvData = savedCardTokenData
      this.setCvvValidity(true)

      if (this.formSubmitCallBack) {
        this.formSubmitCallBack(savedCardTokenData)
        this.formSubmitCallBack = null
      }
    } catch (error) {
      this.trackPaymetricSavedCardCvvError({
        errorReport: 'Error submitting Paymetric saved-card CVV iframe',
        errorData: error,
      })
      this.setCvvValidity(false)
      this.formSubmitCallBack = null
      await this.retryPaymetricSavedCardCvvSession()
    }
  }
}

export { PaymetricSavedCardCvvContainer }
